import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Bug, ChevronDown, ChevronRight, Folder, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { compareTestKeys, executionMarkTone, isIssuePanel } from "../shared";
import { api, forgeContext, invalidateApiCache, openFullApp, uploadArtifact } from "./api";
import productIcon from "../../public/assets/icon.png";
import {
  Alert,
  ConfigNav,
  DonutChart,
  Empty,
  Field,
  Loading,
  Lozenge,
  Modal,
  PageHead,
  Progress,
  Stat,
  ThemeSwitch,
  cx,
  labelize,
  type Theme,
  type Tone,
} from "./ui";

type Project = { jira_project_id: string; jira_project_key: string; name: string };
type FolderRow = { id: string; name: string; parent_id: string | null; materialized_path: string; description: string | null };
type CaseRow = {
  id: string;
  testKey: string;
  title: string;
  priority: string;
  status: string;
  automationStatus: string;
  flakinessScore: number;
  tags: string[];
  folderId: string | null;
  testType: string;
};
type SharedView = {
  id: string;
  title: string;
  version: number;
  steps: Array<{ order: number; action: string; data: string; expected: string }>;
};
type Step = {
  order: number;
  action: string;
  data: string;
  expected: string;
  shared_step_id: string | null;
  called_case_id: string | null;
  shared?: SharedView | null;
  source?: string;
};
type RepositorySnapshot = { projectId: string; folders: FolderRow[]; cases: CaseRow[] };
type CaseDetail = CaseRow & {
  preconditions: string;
  steps?: Step[];
  dataset?: Array<Record<string, string>>;
  latestVersion: number;
  linkedIssues: Array<{ issueKey: string }>;
  severity: string;
};
type Cycle = {
  id: string;
  cycleKey: string;
  name: string;
  environment: string;
  description?: string | null;
  status?: string;
  configurations: Array<Record<string, string>>;
  rollup: { total: number; pass: number; fail: number; blocked?: number; skipped?: number };
};
type ExecutionRow = { id: string; testKey: string; title: string; status: string; configLabel: string; datasetRowIndex: number };
type Plan = { id: string; plan_key: string; name: string; description: string | null; filter_json: string; created_at: string };

const TABS = [
  { id: "overview", href: "/", label: "Overview" },
  { id: "cases", href: "/library", label: "Test Cases" },
  { id: "reviews", href: "/reviews", label: "Reviews" },
  { id: "plans", href: "/plans", label: "Plans" },
  { id: "cycles", href: "/cycles", label: "Runs" },
  { id: "reports", href: "/reports", label: "Reports" },
];

type Slice = { id: string; count: number };
type Pack = {
  projects: Project[];
  reviewWorkflow: boolean;
  reviewCount: number;
  projectId: string;
  view: string;
  data: Record<string, unknown> | null;
};

function specFor(path: string): { view: string; testKey: string; executionId: string; issueKey: string } {
  if (path.startsWith("/cases/")) return { view: "case", testKey: decodeURIComponent(path.split("/")[2] ?? ""), executionId: "", issueKey: "" };
  if (path.startsWith("/run/")) return { view: "run", testKey: "", executionId: path.split("/")[2] ?? "", issueKey: "" };
  if (path.startsWith("/issue/")) return { view: "issue", testKey: "", executionId: "", issueKey: decodeURIComponent(path.split("/")[2] ?? "") };
  if (path === "/reviews") return { view: "reviews", testKey: "", executionId: "", issueKey: "" };
  if (path === "/shared") return { view: "shared", testKey: "", executionId: "", issueKey: "" };
  if (path.startsWith("/cycles")) return { view: "cycles", testKey: "", executionId: "", issueKey: "" };
  if (path.startsWith("/plans")) return { view: "plans", testKey: "", executionId: "", issueKey: "" };
  if (path === "/sessions") return { view: "sessions", testKey: "", executionId: "", issueKey: "" };
  if (path === "/reports" || path === "/analytics") return { view: "reports", testKey: "", executionId: "", issueKey: "" };
  if (path === "/keys") return { view: "keys", testKey: "", executionId: "", issueKey: "" };
  if (path === "/settings") return { view: "settings", testKey: "", executionId: "", issueKey: "" };
  if (path === "/library" || path === "/library/new") return { view: "library", testKey: "", executionId: "", issueKey: "" };
  return { view: "overview", testKey: "", executionId: "", issueKey: "" };
}

function tabFor(path: string): string {
  if (path.startsWith("/library") || path.startsWith("/cases/")) return "cases";
  if (path.startsWith("/cycles") || path.startsWith("/run/")) return "cycles";
  if (path.startsWith("/plans")) return "plans";
  if (path.startsWith("/reviews")) return "reviews";
  if (path.startsWith("/reports") || path.startsWith("/analytics")) return "reports";
  if (path === "/settings" || path === "/keys" || path === "/shared" || path === "/sessions") return "config";
  return "overview";
}

export function App() {
  const { path, navigate } = useHash();
  const [theme, setTheme] = useState<Theme>(() => {
    const query = new URLSearchParams(window.location.search).get("theme");
    const saved = query ?? localStorage.getItem("northline-theme");
    return saved === "dark" || saved === "sepia" || saved === "light" ? saved : "light";
  });
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectId, setProjectId] = useState(localStorage.getItem("northline-project") ?? "");
  const [reviewOn, setReviewOn] = useState(true);
  const [bootError, setBootError] = useState("");
  const [mode, setMode] = useState<"pending" | "workspace" | "panel">("pending");
  const [booted, setBooted] = useState(false);
  const [pack, setPack] = useState<Pack | null>(null);
  const [issueKey, setIssueKey] = useState("");
  const [panelProject, setPanelProject] = useState<{ id: string; key: string } | null>(null);
  const [reviewCount, setReviewCount] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const [cycleId, setCycleId] = useState("");
  const [projectPick, setProjectPick] = useState(0);
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  useEffect(() => {
    document.documentElement.classList.remove("dark", "sepia");
    if (theme !== "light") document.documentElement.classList.add(theme);
    localStorage.setItem("northline-theme", theme);
  }, [theme]);

  const reload = useCallback(() => {
    invalidateApiCache();
    setReloadToken((current) => current + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const ctx = await forgeContext();
        if (cancelled) return;
        const extension = (ctx?.extension ?? {}) as {
          type?: string;
          issue?: { key?: string };
          project?: { id?: string | number; key?: string };
        };
        const ctxProjectId = extension.project?.id != null ? String(extension.project.id) : "";
        const ctxProjectKey = extension.project?.key ?? "";
        if (isIssuePanel(ctx as { moduleKey?: unknown; extension?: { type?: unknown } } | null)) {
          setIssueKey(extension.issue?.key ?? "");
          if (ctxProjectId && ctxProjectKey) setPanelProject({ id: ctxProjectId, key: ctxProjectKey });
          setMode("panel");
          return;
        }
        setMode("workspace");
        const spec = specFor(path);
        const params = new URLSearchParams({ view: spec.view });
        const preferred = projectIdRef.current || ctxProjectId || localStorage.getItem("northline-project") || "";
        if (preferred) params.set("projectId", preferred);
        if (ctxProjectId && ctxProjectKey && preferred === ctxProjectId) {
          params.set("projectKey", ctxProjectKey);
          params.set("projectName", ctxProjectKey);
        }
        if (spec.testKey) params.set("testKey", spec.testKey);
        if (spec.executionId) params.set("executionId", spec.executionId);
        if (spec.issueKey) params.set("issueKey", spec.issueKey);
        if (spec.view === "cycles" && cycleId) params.set("cycleId", cycleId);
        const next = await api<Pack>(`/api/v1/workspace?${params}`);
        if (cancelled) return;
        const list = Array.isArray(next.projects) ? next.projects : [];
        setProjects(list);
        setReviewOn(next.reviewWorkflow ?? true);
        setReviewCount(next.reviewCount ?? 0);
        setPack(next);
        setBooted(true);
        setBootError("");
        const resolved = next.projectId && list.some((item) => item.jira_project_id === next.projectId) ? next.projectId : list[0]?.jira_project_id || "";
        setProjectId((current) => (current === resolved ? current : resolved));
      } catch (error) {
        if (cancelled) return;
        setMode("workspace");
        setBooted(true);
        setBootError(error instanceof Error ? error.message : "The workspace could not be loaded.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [path, reloadToken, cycleId, projectPick]);

  const project = projects.find((item) => item.jira_project_id === projectId) ?? projects[0];

  if (path.startsWith("/share/")) {
    return (
      <main className="app-shell">
        <SharePage token={path.split("/")[2] ?? ""} theme={theme} setTheme={setTheme} />
      </main>
    );
  }

  if (mode !== "workspace") {
    return (
      <div className="app-shell panel-app">
        <a className="skip-link" href="#main">Skip to content</a>
        <header className="panel-head">
          <img src={productIcon} alt="" width={20} height={20} />
          <div>
            <strong>Northline</strong>
            <p className="muted">{mode === "panel" ? issueKey || "Coverage" : "Loading"}</p>
          </div>
          {mode === "panel" ? (
            <button className="btn-ghost" style={{ marginLeft: "auto" }} type="button" onClick={() => void openFullApp(panelProject?.key)}>
              Open Northline
            </button>
          ) : null}
        </header>
        <main id="main">
          {mode === "panel" && issueKey ? (
            <CoverageView issueKey={issueKey} projectId={panelProject?.id ?? ""} projectKey={panelProject?.key ?? ""} />
          ) : mode === "panel" ? (
            <p className="muted">Open this panel from a Jira issue to see linked tests.</p>
          ) : (
            <Loading label="Loading Northline…" />
          )}
        </main>
      </div>
    );
  }

  const current = tabFor(path);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">Skip to content</a>
      <div className="chrome">
        <header className="topbar">
          <a className="brand" href="#/">
            <img src={productIcon} alt="" width={24} height={24} />
            <strong>Northline</strong>
          </a>
          <label className="project-select">
            Project
            <select
              className="field"
              value={project?.jira_project_id ?? ""}
              disabled={projects.length === 0}
              onChange={(event) => {
                setProjectId(event.target.value);
                localStorage.setItem("northline-project", event.target.value);
                setProjectPick((current) => current + 1);
                navigate("/");
              }}
            >
              {projects.length === 0 ? <option value="">Loading projects…</option> : null}
              {projects.map((item) => (
                <option key={item.jira_project_id} value={item.jira_project_id}>
                  {item.jira_project_key} · {item.name}
                </option>
              ))}
            </select>
          </label>
          <div className="topbar-actions">
            <ThemeSwitch theme={theme} setTheme={setTheme} />
            <a className="btn-ghost" href="#/settings" aria-current={current === "config" ? "page" : undefined}>
              Configuration
            </a>
          </div>
        </header>
        <nav className="tabs" aria-label="Northline">
          {TABS.map((item) => (
            <a key={item.id} href={`#${item.href}`} aria-current={current === item.id ? "page" : undefined}>
              {item.label}
              {item.id === "reviews" && reviewCount > 0 ? <span className="tab-count">{reviewCount}</span> : null}
            </a>
          ))}
        </nav>
      </div>
      <main id="main">
        {!booted && !bootError ? <Loading label="Loading Northline…" /> : null}
        {booted && !bootError && !project ? (
          <div className="canvas">
            <Empty title="No project yet" body="Open Northline from a Jira project to start its test library." />
          </div>
        ) : null}
        {bootError ? (
          <div className="canvas">
            <Alert tone="danger">{bootError}</Alert>
          </div>
        ) : null}
        {booted && !bootError && project ? (
          <Routes
            path={path}
            navigate={navigate}
            project={project}
            reviewOn={reviewOn}
            reload={reload}
            theme={theme}
            pack={pack && pack.projectId === project.jira_project_id ? pack : null}
            onCycle={setCycleId}
          />
        ) : null}
      </main>
    </div>
  );
}

function Routes(props: {
  path: string;
  navigate: (to: string) => void;
  project: Project;
  reviewOn: boolean;
  reload: () => void;
  theme: Theme;
  pack: Pack | null;
  onCycle: (id: string) => void;
}) {
  const { path, pack, project, navigate, reviewOn, reload, theme, onCycle } = props;
  const ready = pack?.view === specFor(path).view && pack.data;
  if (!ready) return <Loading label="Loading Northline…" />;
  const data = pack.data ?? {};
  if (path.startsWith("/cases/")) {
    return (
      <CasePage
        testKey={decodeURIComponent(path.split("/")[2] ?? "")}
        project={project}
        theme={theme}
        reload={reload}
        detail={data as CaseDetail & { versions?: Array<{ version: number; changeSummary: string | null; steps: Step[] }> }}
      />
    );
  }
  if (path.startsWith("/run/")) return <WizardPage executionId={path.split("/")[2] ?? ""} initial={data as ExecutionView} />;
  if (path.startsWith("/issue/")) {
    return <IssuePage issueKey={decodeURIComponent(path.split("/")[2] ?? "")} project={project} navigate={navigate} initial={data as CoveragePayload} />;
  }
  if (path === "/reviews") return <ReviewsPage initial={(data.reviews as ReviewItem[]) ?? []} reload={reload} />;
  if (path === "/shared") return <SharedStepsPage project={project} path={path} initial={(data.sharedSteps as SharedRow[]) ?? []} reload={reload} />;
  if (path === "/cycles" || path === "/cycles/new") {
    return (
      <CyclesPage
        project={project}
        navigate={navigate}
        openCreate={path === "/cycles/new"}
        initial={data as { cycles: Cycle[]; openId: string; executions: ExecutionRow[] }}
        onCycle={onCycle}
        reload={reload}
      />
    );
  }
  if (path === "/plans" || path === "/plans/new") {
    return <PlansPage project={project} navigate={navigate} openCreate={path === "/plans/new"} initial={(data.plans as Plan[]) ?? []} reload={reload} />;
  }
  if (path === "/sessions") return <SessionsPage project={project} path={path} initial={(data.sessions as SessionRow[]) ?? []} reload={reload} />;
  if (path === "/reports" || path === "/analytics") return <ReportsPage project={project} initial={data as ReportData} />;
  if (path === "/keys") return <KeysPage path={path} initial={(data.keys as KeyRow[]) ?? []} reload={reload} />;
  if (path === "/settings") {
    return <SettingsPage path={path} project={project} reviewOn={reviewOn} reload={reload} initial={(data.fields as FieldRow[]) ?? []} />;
  }
  if (path === "/library" || path === "/library/new") {
    return (
      <RepositoryPage
        project={project}
        navigate={navigate}
        folders={(data.folders as FolderRow[]) ?? []}
        cases={(data.cases as Array<CaseRow & { links: string[]; stepCount: number; latestVersion: number; preconditions: string }>) ?? []}
        reload={reload}
        openCreate={path === "/library/new"}
      />
    );
  }
  return (
    <OverviewPage
      project={project}
      navigate={navigate}
      analytics={(data.analytics as OverviewPageProps["analytics"]) ?? null}
      cycles={(data.cycles as Cycle[]) ?? []}
      reviewCount={pack.reviewCount}
    />
  );
}

type OverviewPageProps = {
  project: Project;
  navigate: (to: string) => void;
  reviewCount: number;
  analytics: {
    automation: { automated: number; total: number; ratio: number };
    flaky: Array<{ testKey: string; title: string; flakinessScore: number }>;
    gaps: Array<{ issueKey: string; summary: string | null; status: string }>;
    release: Array<{ cycleKey: string; name: string; passRate: number; pass: number; fail: number; total: number }>;
  } | null;
  cycles: Cycle[];
};

type ReviewItem = {
  id: string;
  testKey: string | null;
  proposedTitle: string;
  rationale: string | null;
  status: string;
  requestedBy: string;
  diff: Array<{ change: string; left: Step | null; right: Step | null }>;
};
type ExecutionView = {
  testKey: string;
  title: string;
  preconditions: string;
  environment: string;
  configLabel: string;
  status: string;
  cycleKey?: string | null;
  defects?: string[];
  steps: Array<{ step_order: number; action: string; expected: string; data: string; status: string; actual_result: string; attachments: string[]; source?: string }>;
};
type CoveragePayload = {
  status: string;
  failedCount: number;
  passedCount: number;
  totalLinkedTests: number;
  hasFlakyTests: string;
  cases: Array<{ testKey: string; title: string; status: string }>;
};
type ReportData = {
  flaky: Array<{ testKey: string; title: string; flakinessScore: number }>;
  automation: { automated: number; total: number; ratio: number };
  gaps: Array<{ issueKey: string; summary: string | null; status: string }>;
  release: Array<{ cycleKey: string; name: string; passRate: number; pass: number; fail: number }>;
  charts?: { caseStatus: Slice[]; runStatus: Slice[]; automation: Slice[]; feature: Slice[] };
};
type KeyRow = { id: string; name: string; prefix: string; scopes: string[] };
type SessionRow = { id: string; charter: string; mission: string | null; status: string; notes: string };
type SharedRow = { id: string; title: string; latestVersion: number; steps: Step[] };
type FieldRow = { field_key: string; label: string; field_type: string };

function OverviewPage({ project, navigate, analytics, cycles, reviewCount }: OverviewPageProps) {
  const latest = analytics?.release[0];

  return (
    <div className="canvas">
      <PageHead
        title={project.name}
        description="Coverage, recent runs, and the next actions for this project."
        actions={<Lozenge value="info">{project.jira_project_key}</Lozenge>}
      />
      {!analytics ? <Loading label="Loading the overview…" /> : null}
      {analytics ? (
        <>
          <div className="stat-grid">
            <Stat label="Test cases" value={String(analytics.automation.total)} detail="Active cases in this project" />
            <Stat label="Automated" value={`${analytics.automation.ratio}%`} detail={`${analytics.automation.automated} of ${analytics.automation.total} cases`} />
            <Stat label="Open reviews" value={String(reviewCount)} detail="Edits waiting for approval" />
            <Stat label="Latest pass rate" value={latest ? `${latest.passRate}%` : "—"} detail={latest ? `${latest.cycleKey} · ${latest.name}` : "No runs yet"} />
          </div>
          <div className="split" style={{ marginTop: 16 }}>
            <section className="panel">
              <header>
                Recent runs
                <a className="btn-subtle" href="#/cycles">View all</a>
              </header>
              {cycles.length === 0 ? (
                <Empty title="No runs yet" body="Create a run when you are ready to execute the library." action={<button className="btn" type="button" onClick={() => navigate("/cycles/new")}>New run</button>} />
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Name</th>
                      <th>Progress</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cycles.slice(0, 5).map((cycle) => {
                      const result = cycleResult(cycle);
                      return (
                        <tr key={cycle.id} onClick={() => navigate("/cycles")}>
                          <td className="key">{cycle.cycleKey}</td>
                          <td>{cycle.name}</td>
                          <td><Progress pass={cycle.rollup.pass} fail={cycle.rollup.fail} blocked={cycle.rollup.blocked} total={cycle.rollup.total} /></td>
                          <td><Lozenge value={result.value}>{result.label}</Lozenge></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>
            <div className="stack-list">
              <section className="panel">
                <header>Quick actions</header>
                <div className="quick">
                  <a className="btn" href="#/library/new"><Plus size={16} aria-hidden /> New test case</a>
                  <a className="btn-ghost" href="#/cycles/new">New run</a>
                  <a className="btn-ghost" href="#/plans/new">New plan</a>
                  <a className="btn-ghost" href="#/reports">Open reports</a>
                </div>
              </section>
              <section className="panel">
                <header>Needs attention</header>
                <div style={{ padding: 12 }} className="stack-list">
                  {analytics.flaky.length === 0 && analytics.gaps.length === 0 ? <p className="muted">Nothing is on the watchlist.</p> : null}
                  {analytics.flaky.slice(0, 3).map((item) => (
                    <p key={item.testKey}>
                      <a href={`#/cases/${item.testKey}`}>{item.testKey}</a> {item.title} <Lozenge value="FLAKY">Flaky {item.flakinessScore}</Lozenge>
                    </p>
                  ))}
                  {analytics.gaps.slice(0, 3).map((item) => (
                    <p key={item.issueKey}>
                      <a href={`#/issue/${item.issueKey}`}>{item.issueKey}</a> {item.summary} <Lozenge value={item.status} />
                    </p>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function cycleResult(cycle: Cycle): { label: string; value: string } {
  const blocked = cycle.rollup.blocked ?? 0;
  const skipped = cycle.rollup.skipped ?? 0;
  const { total, pass, fail } = cycle.rollup;
  const done = pass + fail + blocked + skipped;
  if (!total || done === 0) return { label: "Not run", value: "UNEXECUTED" };
  if (pass === total) return { label: "Passed", value: "PASS" };
  if (fail > 0 && done === total) return { label: "Failed", value: "FAIL" };
  return { label: "In progress", value: "WIP" };
}

function RepositoryPage({
  project,
  navigate,
  folders,
  cases,
  reload,
  openCreate,
}: {
  project: Project;
  navigate: (to: string) => void;
  folders: FolderRow[];
  cases: Array<CaseRow & { links: string[]; stepCount: number; latestVersion: number; preconditions: string }>;
  reload: () => void;
  openCreate: boolean;
}) {
  const [folderId, setFolderId] = useState("");
  const [descendants, setDescendants] = useState(true);
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("");
  const [automation, setAutomation] = useState("");
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [keySort, setKeySort] = useState<"key" | "key_desc">("key");
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(openCreate);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [deleteFolder, setDeleteFolder] = useState<FolderRow | null>(null);
  const [folderModal, setFolderModal] = useState<null | { mode: "create" | "rename"; parentId: string | null; folder?: FolderRow }>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const didSelect = useRef(false);

  useEffect(() => {
    if (openCreate) setCreating(true);
  }, [openCreate]);

  const visibleCases = useMemo(() => {
    const folder = folders.find((item) => item.id === folderId);
    const copy = cases.filter((testCase) => {
      if (priority && testCase.priority !== priority) return false;
      if (status && testCase.status !== status) return false;
      if (automation && testCase.automationStatus !== automation) return false;
      if (tag && !testCase.tags.some((item) => item.toLowerCase().includes(tag.toLowerCase()))) return false;
      if (q) {
        const haystack = `${testCase.testKey} ${testCase.title} ${testCase.tags.join(" ")}`.toLowerCase();
        if (!haystack.includes(q.toLowerCase())) return false;
      }
      if (folderId) {
        if (!descendants) return testCase.folderId === folderId;
        const owner = folders.find((item) => item.id === testCase.folderId);
        if (!folder || !owner || !owner.materialized_path.startsWith(folder.materialized_path)) return false;
      }
      return true;
    });
    copy.sort((a, b) => compareTestKeys(a.testKey, b.testKey) * (keySort === "key_desc" ? -1 : 1));
    return copy;
  }, [cases, folders, folderId, descendants, priority, status, automation, tag, q, keySort]);

  useEffect(() => {
    if (didSelect.current || visibleCases.length === 0) return;
    didSelect.current = true;
    setSelectedKey(visibleCases[0]?.testKey ?? "");
  }, [visibleCases]);

  const preview = cases.find((testCase) => testCase.testKey === selectedKey) ?? null;
  const counts = useMemo(() => countFolders(folders, cases), [folders, cases]);
  const filterCount = [priority, status, automation, tag].filter(Boolean).length;
  const folderName = (id: string | null) => folders.find((folder) => folder.id === id)?.name ?? "No folder";

  function closeCreate() {
    setCreating(false);
    if (openCreate) navigate("/library");
  }

  async function saveFolder(name: string) {
    if (!folderModal) return;
    if (folderModal.mode === "rename" && folderModal.folder) {
      await api(`/api/v1/folders/${folderModal.folder.id}`, { method: "PATCH", body: { name } });
    } else {
      await api(`/api/v1/projects/${project.jira_project_id}/folders`, {
        method: "POST",
        body: { name, parentId: folderModal.parentId },
      });
    }
    setFolderModal(null);
    reload();
  }

  async function moveFolder(id: string, parentId: string | null) {
    try {
      await api(`/api/v1/folders/${id}/move`, { method: "POST", body: { parentId } });
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not move that folder.");
    }
  }

  return (
    <section>
      <h1 className="sr-only">Test cases</h1>
      <div className="toolbar">
        <button className="btn" type="button" onClick={() => setCreating(true)}>
          <Plus size={16} aria-hidden /> New test case
        </button>
        <button className="btn-ghost" type="button" onClick={() => setFolderModal({ mode: "create", parentId: null })}>
          New folder
        </button>
        <span className="spacer" />
        <label className="search">
          <Search size={16} aria-hidden />
          <input aria-label="Search test cases" value={q} placeholder="Search" onChange={(event) => setQ(event.target.value)} />
        </label>
        <button className="btn-ghost" type="button" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((current) => !current)}>
          Filters{filterCount ? ` (${filterCount})` : ""}
        </button>
      </div>
      {filtersOpen ? (
        <div className="filters">
          <FilterSelect label="Priority" value={priority} onChange={setPriority} options={["", "CRITICAL", "HIGH", "MEDIUM", "LOW"]} />
          <FilterSelect label="Status" value={status} onChange={setStatus} options={["", "DRAFT", "IN_REVIEW", "APPROVED", "DEPRECATED"]} />
          <FilterSelect label="Automation" value={automation} onChange={setAutomation} options={["", "MANUAL", "TO_BE_AUTOMATED", "AUTOMATED"]} />
          <label className="stack">
            Tag
            <input className="field" value={tag} onChange={(event) => setTag(event.target.value)} />
          </label>
          <label className="check">
            <input type="checkbox" checked={descendants} onChange={(event) => setDescendants(event.target.checked)} />
            Include nested cases
          </label>
          <button
            className="btn-subtle"
            type="button"
            onClick={() => {
              setPriority("");
              setStatus("");
              setAutomation("");
              setTag("");
              setQ("");
            }}
          >
            Clear
          </button>
        </div>
      ) : null}
      {error ? <div style={{ padding: 12 }}><Alert tone="danger">{error}</Alert></div> : null}
      {selected.length > 0 ? (
        <form
          className="selection-bar"
          onSubmit={async (event) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            await api("/api/v1/cases/bulk-move", { method: "POST", body: { testKeys: selected, folderId: String(data.get("folderId")) } });
            setSelected([]);
            reload();
          }}
        >
          <span>{selected.length} selected</span>
          <button className="btn-subtle" type="button" onClick={() => setSelected([])}>Clear selection</button>
          <label>
            Move to
            <select name="folderId" className="field" style={{ marginLeft: 8 }} required>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>{folder.name}</option>
              ))}
            </select>
          </label>
          <button className="btn" type="submit" disabled={folders.length === 0}>Move</button>
        </form>
      ) : null}
      <div className={cx("library", selectedKey && "with-inspector")}>
        <aside className="tree-pane" aria-label="Folders">
          <button type="button" className="tree-all" aria-pressed={folderId === ""} onClick={() => setFolderId("")}>
            <Folder size={16} aria-hidden />
            <span style={{ flex: 1 }}>All test cases</span>
            <span className="tree-count">{cases.length}</span>
          </button>
          <FolderTree
            folders={folders}
            counts={counts}
            selected={folderId}
            collapsed={collapsed}
            onToggle={(id) => setCollapsed((current) => ({ ...current, [id]: !current[id] }))}
            onSelect={setFolderId}
            onMove={moveFolder}
            onAddChild={(id) => setFolderModal({ mode: "create", parentId: id })}
            onRename={(folder) => setFolderModal({ mode: "rename", parentId: folder.parent_id, folder })}
            onDelete={setDeleteFolder}
          />
        </aside>
        <div className="table-pane">
          {visibleCases.length === 0 ? (
            <Empty
              icon={<Folder size={22} aria-hidden />}
              title={q || filterCount ? "No matching test cases" : "No test cases here"}
              body={q || filterCount ? "Clear the search or filters, or create a test case in this folder." : "Create a test case to start the library."}
              action={<button className="btn" type="button" onClick={() => setCreating(true)}>New test case</button>}
            />
          ) : (
            <table className="data-table">
              <caption className="sr-only">Test cases</caption>
              <thead>
                <tr>
                  <th />
                  <th aria-sort={keySort === "key" ? "ascending" : "descending"}>
                    <button type="button" className="btn-subtle" onClick={() => setKeySort((current) => (current === "key" ? "key_desc" : "key"))}>
                      Key
                    </button>
                  </th>
                  <th>Name</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Automation</th>
                  <th>Flaky</th>
                </tr>
              </thead>
              <tbody>
                {visibleCases.map((testCase) => (
                  <tr
                    key={testCase.id}
                    aria-selected={selectedKey === testCase.testKey}
                    onClick={() => setSelectedKey(testCase.testKey)}
                    onDoubleClick={() => navigate(`/cases/${testCase.testKey}`)}
                  >
                    <td onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${testCase.testKey}`}
                        checked={selected.includes(testCase.testKey)}
                        onChange={(event) =>
                          setSelected((current) =>
                            event.target.checked ? [...current, testCase.testKey] : current.filter((key) => key !== testCase.testKey),
                          )
                        }
                      />
                    </td>
                    <td className="key" onClick={(event) => event.stopPropagation()}>
                      <a href={`#/cases/${testCase.testKey}`}>{testCase.testKey}</a>
                    </td>
                    <td className="clip" title={testCase.title}>{testCase.title}</td>
                    <td><Lozenge value={testCase.priority} /></td>
                    <td><Lozenge value={testCase.status} /></td>
                    <td><Lozenge value={testCase.automationStatus} /></td>
                    <td>{testCase.flakinessScore > 25 ? <Lozenge value="FLAKY">Flaky {testCase.flakinessScore}</Lozenge> : <span className="muted">{testCase.flakinessScore || "—"}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {selectedKey ? (
          <aside className="inspector" aria-label="Test case details">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <p className="muted mono">{preview?.testKey ?? selectedKey}</p>
              <button className="icon-btn" type="button" aria-label="Close details" onClick={() => setSelectedKey("")}>
                <X size={16} aria-hidden />
              </button>
            </div>
            {preview ? (
              <>
                <h2>{preview.title}</h2>
                <div className="meta">
                  <Lozenge value={preview.status} />
                  <Lozenge value={preview.priority} />
                  <Lozenge value={preview.automationStatus} />
                </div>
                <dl className="kv">
                  <dt>Folder</dt>
                  <dd>{folderName(preview.folderId)}</dd>
                  <dt>Version</dt>
                  <dd>v{preview.latestVersion}</dd>
                  <dt>Steps</dt>
                  <dd>{preview.stepCount}</dd>
                  <dt>Linked</dt>
                  <dd>{preview.links.length ? preview.links.join(", ") : "None"}</dd>
                </dl>
                {preview.preconditions ? <p className="clamp">{preview.preconditions}</p> : <p className="muted">No preconditions.</p>}
                {preview.tags.length > 0 ? (
                  <div className="meta" style={{ marginTop: 12 }}>
                    {preview.tags.map((item) => <Lozenge key={item}>{item}</Lozenge>)}
                  </div>
                ) : null}
                <a className="btn" style={{ marginTop: 16 }} href={`#/cases/${preview.testKey}`}>Open</a>
              </>
            ) : (
              <p className="muted">Loading details…</p>
            )}
          </aside>
        ) : null}
      </div>
      {creating ? (
        <Modal title="New test case" onClose={closeCreate}>
          <CaseForm
            folderId={folderId}
            onCancel={closeCreate}
            onCreate={async (body) => {
              const created = await api<CaseDetail>(`/api/v1/projects/${project.jira_project_id}/cases`, { method: "POST", body });
              closeCreate();
              navigate(`/cases/${created.testKey}`);
            }}
          />
        </Modal>
      ) : null}
      {folderModal ? (
        <FolderNameModal
          title={folderModal.mode === "rename" ? "Rename folder" : "New folder"}
          initial={folderModal.folder?.name ?? ""}
          onClose={() => setFolderModal(null)}
          onSave={saveFolder}
        />
      ) : null}
      {deleteFolder ? (
        <Modal title={`Delete ${deleteFolder.name}`} onClose={() => setDeleteFolder(null)}>
          <p>Move the cases in this folder, or archive them as deprecated.</p>
          <form
            className="stack-list"
            onSubmit={async (event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              await api(`/api/v1/folders/${deleteFolder.id}`, {
                method: "DELETE",
                body: { action: String(data.get("action")), targetFolderId: String(data.get("target") || "") },
              });
              setDeleteFolder(null);
              setFolderId("");
              reload();
            }}
          >
            <label className="stack">
              Action
              <select name="action" className="field">
                <option value="move">Move cases</option>
                <option value="archive">Archive cases</option>
              </select>
            </label>
            <label className="stack">
              Target folder
              <select name="target" className="field">
                {folders.filter((folder) => folder.id !== deleteFolder.id).map((folder) => (
                  <option key={folder.id} value={folder.id}>{folder.name}</option>
                ))}
              </select>
            </label>
            <button className="btn" type="submit">Delete folder</button>
          </form>
        </Modal>
      ) : null}
    </section>
  );
}

function countFolders(folders: FolderRow[], cases: CaseRow[]): Map<string, number> {
  const totals = new Map<string, number>();
  for (const folder of folders) {
    const marker = `/${folder.id}/`;
    let count = 0;
    for (const testCase of cases) {
      if (!testCase.folderId) continue;
      const owner = folders.find((item) => item.id === testCase.folderId);
      if (!owner) continue;
      if (owner.id === folder.id || owner.materialized_path.includes(marker)) count += 1;
    }
    totals.set(folder.id, count);
  }
  return totals;
}

function FolderTree(props: {
  folders: FolderRow[];
  counts: Map<string, number>;
  selected: string;
  collapsed: Record<string, boolean>;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onMove: (id: string, parentId: string | null) => Promise<void>;
  onAddChild: (id: string) => void;
  onRename: (folder: FolderRow) => void;
  onDelete: (folder: FolderRow) => void;
}) {
  const byParent = useMemo(() => {
    const map = new Map<string | null, FolderRow[]>();
    for (const folder of props.folders) {
      const key = folder.parent_id;
      map.set(key, [...(map.get(key) ?? []), folder]);
    }
    return map;
  }, [props.folders]);

  const render = (parentId: string | null, depth: number): ReactNode =>
    (byParent.get(parentId) ?? []).map((folder) => {
      const children = byParent.get(folder.id) ?? [];
      const open = !props.collapsed[folder.id];
      return (
        <div key={folder.id}>
          <div
            className={cx("tree-item", props.selected === folder.id && "selected")}
            style={{ paddingLeft: depth * 12 }}
            draggable
            onDragStart={(event) => event.dataTransfer.setData("folder", folder.id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const id = event.dataTransfer.getData("folder");
              if (id && id !== folder.id) void props.onMove(id, folder.id);
            }}
          >
            {children.length > 0 ? (
              <button className="icon-btn" type="button" aria-label={open ? `Collapse ${folder.name}` : `Expand ${folder.name}`} onClick={() => props.onToggle(folder.id)}>
                {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
              </button>
            ) : (
              <span style={{ width: 24 }} />
            )}
            <button type="button" className="name" onClick={() => props.onSelect(folder.id)}>{folder.name}</button>
            <span className="tree-count">{props.counts.get(folder.id) ?? 0}</span>
            <span className="row-actions">
              <button className="icon-btn" type="button" aria-label={`Add subfolder of ${folder.name}`} onClick={() => props.onAddChild(folder.id)}>
                <Plus size={14} aria-hidden />
              </button>
              <button className="icon-btn" type="button" aria-label={`Rename ${folder.name}`} onClick={() => props.onRename(folder)}>
                <Pencil size={14} aria-hidden />
              </button>
              <button className="icon-btn" type="button" aria-label={`Delete ${folder.name}`} onClick={() => props.onDelete(folder)}>
                <Trash2 size={14} aria-hidden />
              </button>
            </span>
          </div>
          {open ? render(folder.id, depth + 1) : null}
        </div>
      );
    });

  return <div>{render(null, 0)}</div>;
}

function FolderNameModal({ title, initial, onClose, onSave }: { title: string; initial: string; onClose: () => void; onSave: (name: string) => Promise<void> }) {
  const [name, setName] = useState(initial);
  const [error, setError] = useState("");
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn-ghost" type="button" onClick={onClose}>Cancel</button>
          <button className="btn" type="submit" form="folder-name-form">Save</button>
        </>
      }
    >
      <form
        id="folder-name-form"
        onSubmit={async (event) => {
          event.preventDefault();
          try {
            await onSave(name.trim());
          } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save the folder.");
          }
        }}
      >
        <label className="stack">
          Name
          <input className="field" value={name} required onChange={(event) => setName(event.target.value)} />
        </label>
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </form>
    </Modal>
  );
}

function FilterSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: string[] }) {
  return (
    <label className="stack">
      {label}
      <select className="field" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option || "any"} value={option}>{option ? labelize(option) : "Any"}</option>
        ))}
      </select>
    </label>
  );
}

function CaseForm({
  folderId,
  onCreate,
  onCancel,
}: {
  folderId: string;
  onCreate: (body: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
}) {
  const [error, setError] = useState("");
  return (
    <form
      className="stack-list"
      onSubmit={async (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        try {
          await onCreate({
            folderId: folderId || null,
            title: String(data.get("title")),
            preconditions: String(data.get("preconditions") || ""),
            priority: String(data.get("priority")),
            steps: [{ action: String(data.get("action")), expected: String(data.get("expected") || "") }],
            tags: String(data.get("tags") || "").split(",").map((tag) => tag.trim()).filter(Boolean),
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : "Could not create the case.");
        }
      }}
    >
      <Field name="title" label="Name" required />
      <label className="stack">
        Preconditions
        <textarea className="field" name="preconditions" />
      </label>
      <Field name="action" label="First step" required />
      <Field name="expected" label="Expected result" />
      <label className="stack">
        Priority
        <select name="priority" className="field" defaultValue="MEDIUM">
          {["CRITICAL", "HIGH", "MEDIUM", "LOW"].map((item) => <option key={item} value={item}>{labelize(item)}</option>)}
        </select>
      </label>
      <Field name="tags" label="Tags, comma separated" />
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="page-actions">
        <button className="btn-ghost" type="button" onClick={onCancel}>Cancel</button>
        <button className="btn" type="submit">Create</button>
      </div>
    </form>
  );
}

function CasePage({
  testKey,
  project,
  theme,
  detail,
  reload,
}: {
  testKey: string;
  project: Project;
  theme: Theme;
  detail: CaseDetail & { versions?: Array<{ version: number; changeSummary: string | null; steps: Step[] }> };
  reload: () => void;
}) {
  const versions = detail.versions ?? [];
  const [from, setFrom] = useState(versions[0]?.version ?? 1);
  const [to, setTo] = useState(versions[versions.length - 1]?.version ?? 1);
  const [diff, setDiff] = useState<Array<{ order: number; change: string; left: Step | null; right: Step | null }>>([]);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [title, setTitle] = useState(detail.title);
  const [preconditions, setPreconditions] = useState(detail.preconditions);
  const [steps, setSteps] = useState<Step[]>(detail.steps ?? []);
  const [datasetText, setDatasetText] = useState(JSON.stringify(detail.dataset ?? [], null, 2));
  const [tab, setTab] = useState<"details" | "script" | "trace" | "history">("script");

  useEffect(() => {
    const history = detail.versions ?? [];
    setTitle(detail.title);
    setPreconditions(detail.preconditions);
    setSteps(detail.steps ?? []);
    setDatasetText(JSON.stringify(detail.dataset ?? [], null, 2));
    setFrom(history[0]?.version ?? 1);
    setTo(history[history.length - 1]?.version ?? 1);
  }, [detail]);

  function updateStep(index: number, patch: Partial<Step>) {
    setSteps((current) => current.map((step, stepIndex) => (stepIndex === index ? { ...step, ...patch } : step)));
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      const dataset = JSON.parse(datasetText) as Array<Record<string, string>>;
      const saved = await api<{ write?: { mode: string; version: number } }>(`/api/v1/cases/${testKey}`, {
        method: "PATCH",
        body: { title, preconditions, steps, dataset, changeSummary: "Edited in the workspace" },
      });
      setNote(saved.write?.mode === "review" ? "Sent to review. The approved version is unchanged." : `Saved as version ${saved.write?.version ?? detail.latestVersion}.`);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    }
  }

  return (
    <div className="canvas">
      <p className="crumbs">
        <a href="#/library">Test cases</a>
        <span>/</span>
        <span>{testKey}</span>
      </p>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {detail ? (
        <form id="case-form" onSubmit={(event) => void save(event)} className={theme === "sepia" ? "reader" : undefined}>
          <div className="case-bar">
            <strong className="mono">{detail.testKey}</strong>
            <Lozenge value={detail.status} />
            <Lozenge value={detail.priority} />
            <Lozenge value={detail.automationStatus} />
            <span className="muted">v{detail.latestVersion}</span>
            {detail.flakinessScore > 25 ? <Lozenge value="FLAKY">{detail.flakinessScore}</Lozenge> : null}
            <span className="spacer" />
            <button className="btn" type="submit">Save</button>
          </div>
          <label className="sr-only" htmlFor="case-title">Name</label>
          <input id="case-title" className="title-input" value={title} onChange={(event) => setTitle(event.target.value)} />
          {note ? <Alert tone="success">{note}</Alert> : null}
          <div className="subnav" role="tablist" aria-label="Test case sections">
            {([
              ["details", "Details"],
              ["script", "Test script"],
              ["trace", "Traceability"],
              ["history", "History"],
            ] as const).map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}>{label}</button>
            ))}
          </div>
          {tab === "details" ? (
            <div className="detail-grid">
              <label className="stack">
                Preconditions
                <textarea className="field" value={preconditions} onChange={(event) => setPreconditions(event.target.value)} />
              </label>
              <aside className="side-card">
                <h2>Details</h2>
                <dl className="kv">
                  <dt>Status</dt><dd><Lozenge value={detail.status} /></dd>
                  <dt>Priority</dt><dd><Lozenge value={detail.priority} /></dd>
                  <dt>Automation</dt><dd><Lozenge value={detail.automationStatus} /></dd>
                  <dt>Type</dt><dd>{labelize(detail.testType || "")}</dd>
                  <dt>Project</dt><dd>{project.jira_project_key}</dd>
                  <dt>Tags</dt><dd>{detail.tags.join(", ") || "None"}</dd>
                </dl>
                <label className="stack">
                  Dataset JSON
                  <textarea className="field mono" value={datasetText} onChange={(event) => setDatasetText(event.target.value)} />
                </label>
                <p className="muted" style={{ marginTop: 8 }}>Use {"{{variable}}"} in steps. Each dataset row is its own iteration in a run.</p>
              </aside>
            </div>
          ) : null}
          {tab === "script" ? (
            <>
              <table className="steps">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>#</th>
                    <th>Action</th>
                    <th>Test data</th>
                    <th>Expected result</th>
                  </tr>
                </thead>
                <tbody>
                  {steps.map((step, index) => (
                    <tr key={`${step.order}-${step.shared_step_id ?? "case"}-${index}`}>
                      <td>{index + 1}</td>
                      {step.shared_step_id ? (
                        <td colSpan={3}><SharedStepBody step={step} index={index} /></td>
                      ) : (
                        <>
                          <td><textarea className="field" aria-label={`Action for step ${index + 1}`} value={step.action} onChange={(event) => updateStep(index, { action: event.target.value })} /></td>
                          <td><input className="field" aria-label={`Test data for step ${index + 1}`} value={step.data} onChange={(event) => updateStep(index, { data: event.target.value })} /></td>
                          <td><textarea className="field" aria-label={`Expected result for step ${index + 1}`} value={step.expected} onChange={(event) => updateStep(index, { expected: event.target.value })} /></td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                className="btn-ghost"
                style={{ marginTop: 12 }}
                type="button"
                onClick={() => setSteps((current) => [...current, { order: current.length + 1, action: "", data: "", expected: "", shared_step_id: null, called_case_id: null }])}
              >
                Add step
              </button>
            </>
          ) : null}
          {tab === "trace" ? (
            <section className="panel">
              <header>Linked work items</header>
              {detail.linkedIssues.length === 0 ? (
                <Empty title="No linked work items" body="Link this case from a Jira issue, or from the issue coverage view." />
              ) : (
                <ul>
                  {detail.linkedIssues.map((issue) => (
                    <li key={issue.issueKey} style={{ padding: "8px 12px" }}>
                      <a href={`#/issue/${issue.issueKey}`}>{issue.issueKey}</a>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}
          {tab === "history" ? (
            <section>
              <div className="toolbar" style={{ paddingLeft: 0 }}>
                <label className="stack">
                  From
                  <select className="field" value={from} onChange={(event) => setFrom(Number(event.target.value))}>
                    {versions.map((version) => <option key={version.version} value={version.version}>v{version.version}</option>)}
                  </select>
                </label>
                <label className="stack">
                  To
                  <select className="field" value={to} onChange={(event) => setTo(Number(event.target.value))}>
                    {versions.map((version) => <option key={`to-${version.version}`} value={version.version}>v{version.version}</option>)}
                  </select>
                </label>
                <button
                  className="btn-ghost"
                  type="button"
                  onClick={async () => {
                    const result = await api<{ rows: typeof diff }>(`/api/v1/cases/${testKey}/diff?from=${from}&to=${to}`);
                    setDiff(result.rows);
                  }}
                >
                  Compare
                </button>
              </div>
              {diff.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr><th>Change</th><th>From</th><th>To</th></tr>
                  </thead>
                  <tbody>
                    {diff.map((row) => (
                      <tr key={row.order}>
                        <td>{labelize(row.change)}</td>
                        <td>{row.left?.action ?? "—"}</td>
                        <td>{row.right?.action ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="muted">Choose two versions and compare their steps.</p>}
            </section>
          ) : null}
        </form>
      ) : !error ? <Loading label="Loading the test case…" /> : null}
    </div>
  );
}

function ReviewsPage({ initial, reload }: { initial: ReviewItem[]; reload: () => void }) {
  const [error, setError] = useState("");
  async function decide(id: string, action: "approve" | "reject") {
    try {
      await api(`/api/v1/reviews/${id}/${action}`, { method: "POST" });
      setError("");
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update that review.");
    }
  }
  return (
    <div className="canvas">
      <PageHead title="Reviews" description="Approving a request publishes it as the active version and records who approved it." />
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {initial.length === 0 ? (
        <div className="panel"><Empty title="No open reviews" body="When review is on, edits wait here until someone approves them." /></div>
      ) : (
        <div className="stack-list">
          {initial.map((review) => (
            <article key={review.id} className="panel" style={{ padding: 16 }}>
              <p className="muted mono">{review.testKey}</p>
              <h2 style={{ fontSize: 16, fontWeight: 650, margin: "4px 0" }}>{review.proposedTitle}</h2>
              <p className="muted">{review.rationale} · requested by {review.requestedBy}</p>
              <ul className="stack-list" style={{ margin: "8px 0" }}>
                {review.diff.filter((row) => row.change !== "same").map((row, index) => (
                  <li key={index}>{labelize(row.change)}: {row.left?.action ?? "—"} → {row.right?.action ?? "—"}</li>
                ))}
              </ul>
              <div className="page-actions">
                <button className="btn" type="button" onClick={() => void decide(review.id, "approve")}>Approve</button>
                <button className="btn-ghost" type="button" onClick={() => void decide(review.id, "reject")}>Reject</button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function CyclesPage({
  project,
  navigate,
  openCreate,
  initial,
  onCycle,
  reload,
}: {
  project: Project;
  navigate: (to: string) => void;
  openCreate: boolean;
  initial: { cycles: Cycle[]; openId: string; executions: ExecutionRow[] };
  onCycle: (id: string) => void;
  reload: () => void;
}) {
  const [creating, setCreating] = useState(openCreate);
  const [error, setError] = useState("");
  useEffect(() => { if (openCreate) setCreating(true); }, [openCreate]);

  function closeCreate() {
    setCreating(false);
    if (openCreate) navigate("/cycles");
  }

  const { cycles, openId: open, executions } = initial;
  const active = cycles.find((cycle) => cycle.id === open);

  return (
    <section>
      <div className="canvas" style={{ paddingBottom: 0 }}>
        <PageHead
          title="Runs"
          description="A run is a planned execution of approved cases, optionally across configurations."
          actions={<button className="btn" type="button" onClick={() => setCreating(true)}><Plus size={16} aria-hidden /> New run</button>}
        />
      </div>
      <div className="toolbar">
        <span className="muted">{cycles.length} {cycles.length === 1 ? "run" : "runs"}</span>
      </div>
      {error ? <div style={{ padding: 12 }}><Alert tone="danger">{error}</Alert></div> : null}
      {cycles.length === 0 ? (
        <Empty title="No runs yet" body="Create a run, then add approved cases. Each configuration becomes its own execution." action={<button className="btn" type="button" onClick={() => setCreating(true)}>New run</button>} />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Name</th>
              <th>Environment</th>
              <th>Progress</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {cycles.map((cycle) => {
              const result = cycleResult(cycle);
              return (
                <tr key={cycle.id} aria-selected={open === cycle.id} onClick={() => { if (cycle.id !== open) onCycle(cycle.id); }}>
                  <td className="key">{cycle.cycleKey}</td>
                  <td>
                    <div>{cycle.name}</div>
                    <div className="muted">{cycle.configurations.map((item) => Object.values(item).join(" / ")).filter(Boolean).join(" · ") || "No matrix"}</div>
                  </td>
                  <td>{cycle.environment}</td>
                  <td><Progress pass={cycle.rollup.pass} fail={cycle.rollup.fail} blocked={cycle.rollup.blocked} total={cycle.rollup.total} /></td>
                  <td><Lozenge value={result.value}>{result.label}</Lozenge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {active ? (
        <section className="panel" style={{ margin: 12 }}>
          <header>
            <span>{active.cycleKey} executions</span>
            <button
              className="btn-ghost"
              type="button"
              onClick={async () => {
                try {
                  await api(`/api/v1/cycles/${active.id}/populate`, { method: "POST", body: {} });
                  setError("");
                  reload();
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Could not add cases.");
                }
              }}
            >
              Add approved cases
            </button>
          </header>
          {executions.length === 0 ? (
            <Empty title="No executions in this run" body="Add approved cases to create one execution per configuration." />
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Key</th><th>Name</th><th>Configuration</th><th>Row</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {executions.map((execution) => (
                  <tr key={execution.id}>
                    <td className="key">{execution.testKey}</td>
                    <td className="clip">{execution.title}</td>
                    <td>{execution.configLabel}</td>
                    <td>{execution.datasetRowIndex}</td>
                    <td><Lozenge value={execution.status} /></td>
                    <td><button className="btn" type="button" onClick={() => navigate(`/run/${execution.id}`)}>Run</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ) : null}
      {creating ? (
        <Modal title="New run" onClose={closeCreate}>
          <form
            className="stack-list"
            onSubmit={async (event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              const browsers = String(data.get("browsers") || "").split(",").map((item) => item.trim()).filter(Boolean);
              const regions = String(data.get("regions") || "").split(",").map((item) => item.trim()).filter(Boolean);
              const axes: Record<string, string[]> = {};
              if (browsers.length) axes.browser = browsers;
              if (regions.length) axes.region = regions;
              try {
                await api(`/api/v1/projects/${project.jira_project_id}/cycles`, {
                  method: "POST",
                  body: { name: String(data.get("name")), environment: String(data.get("environment") || "staging"), axes },
                });
                closeCreate();
                setError("");
                reload();
              } catch (err) {
                setError(err instanceof Error ? err.message : "Could not create the run.");
              }
            }}
          >
            <Field name="name" label="Name" required />
            <Field name="environment" label="Environment" placeholder="staging" />
            <Field name="browsers" label="Browsers" placeholder="Chrome, Firefox" />
            <Field name="regions" label="Regions" placeholder="US, EU" />
            <p className="muted">Each browser and region combination becomes its own execution. Dataset rows multiply again inside each combination.</p>
            <div className="page-actions">
              <button className="btn-ghost" type="button" onClick={closeCreate}>Cancel</button>
              <button className="btn" type="submit">Create</button>
            </div>
          </form>
        </Modal>
      ) : null}
    </section>
  );
}

function PlansPage({
  project,
  navigate,
  openCreate,
  initial,
  reload,
}: {
  project: Project;
  navigate: (to: string) => void;
  openCreate: boolean;
  initial: Plan[];
  reload: () => void;
}) {
  const [creating, setCreating] = useState(openCreate);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { if (openCreate) setCreating(true); }, [openCreate]);
  function closeCreate() {
    setCreating(false);
    if (openCreate) navigate("/plans");
  }
  return (
    <section>
      <h1 className="sr-only">Plans</h1>
      <div className="toolbar">
        <button className="btn" type="button" onClick={() => setCreating(true)}><Plus size={16} aria-hidden /> New plan</button>
      </div>
      <div className="canvas" style={{ paddingTop: 12 }}>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {note ? <Alert tone="success">{note}</Alert> : null}
        <p className="muted" style={{ marginBottom: 12 }}>A plan stores a filter. Creating a run from it adds the matching approved cases.</p>
        {initial.length === 0 ? (
          <div className="panel"><Empty title="No plans yet" body="Save a priority or tag filter, then turn it into a run when you are ready to execute." action={<button className="btn" type="button" onClick={() => setCreating(true)}>New plan</button>} /></div>
        ) : (
          <div className="panel">
            <table className="data-table">
              <thead>
                <tr><th>Key</th><th>Name</th><th>Scope</th><th>Created</th><th /></tr>
              </thead>
              <tbody>
                {initial.map((plan) => (
                  <tr key={plan.id}>
                    <td className="key">{plan.plan_key}</td>
                    <td>
                      <div>{plan.name}</div>
                      {plan.description ? <div className="muted">{plan.description}</div> : null}
                    </td>
                    <td>{describeFilter(plan.filter_json)}</td>
                    <td>{plan.created_at ? new Date(plan.created_at).toLocaleDateString() : "—"}</td>
                    <td>
                      <button
                        className="btn-ghost"
                        type="button"
                        onClick={async () => {
                          const result = await api<{ cycle: { cycleKey: string }; populated: { created: unknown[] } }>(`/api/v1/plans/${plan.id}/materialize`, { method: "POST" });
                          setNote(`Created run ${result.cycle.cycleKey} with ${result.populated.created?.length ?? 0} executions.`);
                          reload();
                        }}
                      >
                        Create run
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {creating ? (
        <Modal title="New plan" onClose={closeCreate}>
          <form
            className="stack-list"
            onSubmit={async (event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              const filter: Record<string, string> = {};
              const priority = String(data.get("priority") || "");
              const tag = String(data.get("tag") || "").trim();
              if (priority) filter.priority = priority;
              if (tag) filter.tag = tag;
              await api(`/api/v1/projects/${project.jira_project_id}/plans`, {
                method: "POST",
                body: { name: String(data.get("name")), description: String(data.get("description") || ""), filter },
              });
              closeCreate();
              reload();
            }}
          >
            <Field name="name" label="Name" required />
            <label className="stack">
              Description
              <textarea className="field" name="description" />
            </label>
            <label className="stack">
              Priority
              <select className="field" name="priority" defaultValue="">
                <option value="">Any</option>
                {["CRITICAL", "HIGH", "MEDIUM", "LOW"].map((item) => <option key={item} value={item}>{labelize(item)}</option>)}
              </select>
            </label>
            <Field name="tag" label="Tag" />
            <div className="page-actions">
              <button className="btn-ghost" type="button" onClick={closeCreate}>Cancel</button>
              <button className="btn" type="submit">Create</button>
            </div>
          </form>
        </Modal>
      ) : null}
    </section>
  );
}

function describeFilter(raw: string): string {
  try {
    const filter = JSON.parse(raw) as Record<string, unknown>;
    const parts = Object.entries(filter)
      .filter(([, value]) => value != null && String(value).length > 0)
      .map(([key, value]) => `${labelize(key)} ${labelize(String(value))}`);
    return parts.join(" · ") || "All approved cases";
  } catch {
    return "All approved cases";
  }
}

function WizardPage({ executionId, initial }: { executionId: string; initial: ExecutionView }) {
  const [view, setView] = useState<ExecutionView | null>(initial);
  const [index, setIndex] = useState(0);
  const [actual, setActual] = useState("");
  const [bug, setBug] = useState<{ summary: string; description: string; issueKey?: string } | null>(null);
  const [error, setError] = useState("");
  const [live, setLive] = useState("");

  useEffect(() => {
    setView(initial);
  }, [initial]);

  const step = view?.steps[index];

  const mark = useCallback(async (status: "PASS" | "FAIL" | "BLOCKED" | "SKIPPED") => {
    if (!step) return;
    try {
      const updated = await api<NonNullable<typeof view>>(`/api/v1/executions/${executionId}/steps/${step.step_order}`, {
        method: "PATCH",
        body: { status, actualResult: actual },
      });
      setView(updated);
      setLive(`${labelize(status)} recorded for step ${step.step_order}`);
      setActual("");
      if (status === "FAIL") {
        setBug({
          summary: `Failed ${updated.testKey}: ${step.action}`,
          description: `Environment: ${updated.environment}\nConfiguration: ${updated.configLabel}\nPreconditions: ${updated.preconditions}\nStep: ${step.action}\nExpected: ${step.expected}\nActual: ${actual || "Not recorded"}`,
        });
      } else if (index < updated.steps.length - 1) setIndex(index + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not record the result.");
    }
  }, [step, executionId, actual, index]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT")) return;
      const key = event.key.toLowerCase();
      if (key === "p") void mark("PASS");
      if (key === "f") void mark("FAIL");
      if (key === "b") void mark("BLOCKED");
      if (key === "s") void mark("SKIPPED");
      if (key === "j") setIndex((current) => Math.min((view?.steps.length ?? 1) - 1, current + 1));
      if (key === "k") setIndex((current) => Math.max(0, current - 1));
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mark, view, index]);

  async function onPaste(event: React.ClipboardEvent) {
    const file = [...event.clipboardData.files].find((item) => item.type.startsWith("image/"));
    if (!file || !step) return;
    event.preventDefault();
    const signed = await api<{ uploadUrl: string; r2Key: string }>("/api/v1/artifacts/sign", {
      method: "POST",
      body: { executionId, filename: file.name || "screenshot.png", contentType: file.type },
    });
    await uploadArtifact(signed.uploadUrl, file, file.type);
    const updated = await api<NonNullable<typeof view>>(`/api/v1/executions/${executionId}/steps/${step.step_order}`, {
      method: "PATCH",
      body: { attachments: [signed.r2Key] },
    });
    setView(updated);
    setLive("Screenshot stored on this step.");
  }

  if (error) return <div className="canvas"><Alert tone="danger">{error}</Alert></div>;
  if (!view || !step) return <Loading label="Loading the run…" />;
  return (
    <div className="canvas" onPaste={(event) => void onPaste(event)}>
      <p className="sr-only" aria-live="polite">{live}</p>
      <p className="crumbs">
        <a href="#/cycles">Runs</a>
        <span>/</span>
        <span>{view.cycleKey || "Run"}</span>
        <span>/</span>
        <span>{view.testKey}</span>
      </p>
      <div className="case-bar">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 650 }}>{view.title}</h1>
          <p className="muted">{view.environment} · {view.configLabel}</p>
        </div>
        <Lozenge value={view.status} />
      </div>
      <div className="player">
        <nav className="step-nav" aria-label="Steps">
          {view.steps.map((item, itemIndex) => (
            <button key={item.step_order} type="button" aria-current={itemIndex === index ? "true" : undefined} onClick={() => setIndex(itemIndex)}>
              <span className={`pip pip-${toneClass(item.status)}`} />
              <span>
                <strong>Step {itemIndex + 1}</strong>
                <span className="clip" style={{ display: "block" }}>{item.action}</span>
              </span>
            </button>
          ))}
        </nav>
        <div className="player-main">
          <p className="muted">
            Step {index + 1} of {view.steps.length}
            {step.source === "shared" ? " · Shared step" : ""}
            {step.source === "called" ? " · Called test" : ""}
          </p>
          <h2>{step.action}</h2>
          {step.data ? <p><span className="muted">Test data: </span>{step.data}</p> : null}
          <p style={{ marginTop: 8 }}><span className="muted">Expected: </span>{step.expected || "No expected result recorded."}</p>
          <p style={{ marginTop: 8 }}>Current result: <Lozenge value={step.status} /></p>
          {step.actual_result ? <p className="muted" style={{ marginTop: 8 }}>Recorded: {step.actual_result}</p> : null}
          {step.attachments.length > 0 ? <p className="muted">{step.attachments.length} attachment(s) stored.</p> : null}
          {(view.defects ?? []).length > 0 ? <p className="muted">Defects: {view.defects?.join(", ")}</p> : null}
          <label className="stack" style={{ marginTop: 16 }}>
            Actual result
            <textarea className="field" value={actual} onChange={(event) => setActual(event.target.value)} />
          </label>
          <div className="page-actions" style={{ marginTop: 12 }} role="group" aria-label="Step result">
            {([
              ["PASS", "Pass"],
              ["FAIL", "Fail"],
              ["BLOCKED", "Blocked"],
              ["SKIPPED", "Skip"],
            ] as const).map(([status, text]) => (
              <button
                key={status}
                className={`mark mark-${executionMarkTone(status)}`}
                type="button"
                aria-pressed={step.status === status}
                onClick={() => void mark(status)}
              >
                {text}
              </button>
            ))}
          </div>
          <p className="keys">P pass · F fail · B blocked · S skip · J next · K previous. Paste a screenshot onto this step.</p>
        </div>
      </div>
      {bug ? (
        <Modal title="File a bug" onClose={() => setBug(null)}>
          <p style={{ whiteSpace: "pre-wrap" }}>{bug.description}</p>
          {bug.issueKey ? (
            <Alert tone="success">Filed {bug.issueKey}</Alert>
          ) : (
            <button
              className="btn"
              type="button"
              onClick={async () => {
                const filed = await api<{ issueKey: string }>(`/api/v1/executions/${executionId}/bug`, { method: "POST", body: { summary: bug.summary } });
                setBug({ ...bug, issueKey: filed.issueKey });
              }}
            >
              <Bug size={16} aria-hidden /> Create bug
            </button>
          )}
        </Modal>
      ) : null}
    </div>
  );
}

function toneClass(status: string): Tone {
  const tone = executionMarkTone(status);
  if (tone === "pass") return "success";
  if (tone === "fail") return "danger";
  if (tone === "blocked") return "orange";
  if (tone === "progress") return "info";
  if (tone === "flaky") return "magenta";
  if (tone === "skip") return "purple";
  return "neutral";
}

const CHART_LABEL: Record<string, string> = {
  PASS: "Passed",
  FAIL: "Failed",
  UNEXECUTED: "Not run",
  NOT_EXECUTED: "Not run",
  BLOCKED: "Blocked",
  SKIPPED: "Skipped",
  WIP: "In progress",
  FLAKY: "Flaky",
  DRAFT: "Draft",
  IN_REVIEW: "In review",
  APPROVED: "Approved",
  DEPRECATED: "Deprecated",
  AUTOMATED: "Automated",
  MANUAL: "Manual",
  TO_BE_AUTOMATED: "To be automated",
};

const STATUS_COLOR: Record<string, string> = {
  PASS: "var(--tone-success)",
  PASSED: "var(--tone-success)",
  FAIL: "var(--tone-danger)",
  FAILED: "var(--tone-danger)",
  UNEXECUTED: "var(--tone-neutral)",
  NOT_EXECUTED: "var(--tone-neutral)",
  BLOCKED: "var(--tone-orange)",
  SKIPPED: "var(--tone-purple)",
  WIP: "var(--tone-info)",
  FLAKY: "var(--tone-magenta)",
  DRAFT: "var(--tone-neutral)",
  IN_REVIEW: "var(--tone-teal)",
  APPROVED: "var(--tone-success)",
  DEPRECATED: "var(--tone-danger)",
  AUTOMATED: "var(--tone-success)",
  MANUAL: "var(--tone-neutral)",
  TO_BE_AUTOMATED: "var(--tone-yellow)",
};

function chartSlices(rows: Slice[] | undefined, feature = false) {
  return (rows ?? []).map((row, index) => ({
    id: row.id,
    label: feature ? row.id : (CHART_LABEL[row.id] ?? labelize(row.id)),
    count: row.count,
    color: feature ? `var(--chart-${(index % 10) + 1})` : (STATUS_COLOR[row.id] ?? `var(--chart-${(index % 10) + 1})`),
  }));
}

function ReportsPage({ project, initial }: { project: Project; initial: ReportData }) {
  const [section, setSection] = useState<"summary" | "release" | "flaky" | "gaps" | "share">("summary");
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const data = initial;
  const sections = [
    ["summary", "Execution summary"],
    ["release", "Release readiness"],
    ["flaky", "Flaky tests"],
    ["gaps", "Coverage gaps"],
    ["share", "Stakeholder link"],
  ] as const;
  return (
    <div className="canvas">
      <PageHead title="Reports" description="Execution, flakiness, and coverage for this project. The stakeholder link is read-only." />
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {data ? (
        <div className="reports">
          <nav aria-label="Report sections">
            {sections.map(([id, label]) => (
              <button key={id} type="button" aria-pressed={section === id} onClick={() => setSection(id)}>{label}</button>
            ))}
          </nav>
          <div className="panel">
            {section === "summary" ? (
              <div className="chart-grid">
                <DonutChart title="Test cases by status" slices={chartSlices(data.charts?.caseStatus)} empty="No test cases in this project." />
                <DonutChart title="Test runs by status" slices={chartSlices(data.charts?.runStatus)} empty="No executions in this project yet." />
                <DonutChart title="Test cases by automation" slices={chartSlices(data.charts?.automation)} empty="No test cases in this project." />
                <DonutChart title="Test cases by feature" slices={chartSlices(data.charts?.feature, true)} empty="No test cases to group by folder." />
              </div>
            ) : null}
            {section === "release" ? (
              data.release.length === 0 ? <Empty title="No runs to report" body="Pass rates appear after a run has executions." /> : (
                <table className="data-table">
                  <thead><tr><th>Run</th><th>Name</th><th>Pass rate</th><th>Passed</th><th>Failed</th></tr></thead>
                  <tbody>
                    {data.release.map((item) => (
                      <tr key={item.cycleKey}>
                        <td className="key">{item.cycleKey}</td>
                        <td>{item.name}</td>
                        <td>{item.passRate}%</td>
                        <td>{item.pass}</td>
                        <td>{item.fail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : null}
            {section === "flaky" ? (
              data.flaky.length === 0 ? <Empty title="Watchlist is clear" body="Cases cross the line when more than a quarter of recent results disagree." /> : (
                <table className="data-table">
                  <thead><tr><th>Key</th><th>Name</th><th>Score</th></tr></thead>
                  <tbody>
                    {data.flaky.map((item) => (
                      <tr key={item.testKey}>
                        <td className="key"><a href={`#/cases/${item.testKey}`}>{item.testKey}</a></td>
                        <td>{item.title}</td>
                        <td><Lozenge value="FLAKY">Flaky {item.flakinessScore}</Lozenge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : null}
            {section === "gaps" ? (
              data.gaps.length === 0 ? <Empty title="No coverage gaps" body="Every tracked work item has a linked test that is not currently failing." /> : (
                <table className="data-table">
                  <thead><tr><th>Work item</th><th>Summary</th><th>Coverage</th></tr></thead>
                  <tbody>
                    {data.gaps.map((item) => (
                      <tr key={item.issueKey}>
                        <td className="key"><a href={`#/issue/${item.issueKey}`}>{item.issueKey}</a></td>
                        <td>{item.summary}</td>
                        <td><Lozenge value={item.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : null}
            {section === "share" ? (
              <div style={{ padding: 16 }} className="stack-list">
                <p>Create a read-only link for the latest run. Anyone with the link can see the rollup. They cannot edit cases.</p>
                <button
                  className="btn"
                  type="button"
                  onClick={async () => {
                    const report = await api<{ url: string }>(`/api/v1/projects/${project.jira_project_id}/reports`, {
                      method: "POST",
                      body: { label: `${project.jira_project_key} stakeholder report` },
                    });
                    setLink(report.url);
                  }}
                >
                  Create report link
                </button>
                {link ? <p className="mono" style={{ wordBreak: "break-all" }}>{link}</p> : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function KeysPage({ path, initial, reload }: { path: string; initial: KeyRow[]; reload: () => void }) {
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  return (
    <div className="canvas">
      <PageHead title="Configuration" description="API keys for CI and MCP clients. The raw key is shown once. Only its SHA-256 hash is stored." />
      <div className="config">
        <ConfigNav path={path} />
        <div className="stack-list">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <form
            className="panel"
            style={{ padding: 16 }}
            onSubmit={async (event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              const scopes = ["mcp:author", "mcp:execute", "ci:ingest"].filter((scope) => data.get(scope) === "on");
              const created = await api<{ token: string }>("/api/v1/api-keys", { method: "POST", body: { name: String(data.get("name")), scopes } });
              setToken(created.token);
              event.currentTarget.reset();
              reload();
            }}
          >
            <div className="toolbar" style={{ padding: 0, border: 0 }}>
              <input className="field" name="name" placeholder="Key name" aria-label="Key name" required />
              <label className="check"><input type="checkbox" name="mcp:author" defaultChecked /> Author</label>
              <label className="check"><input type="checkbox" name="mcp:execute" defaultChecked /> Execute</label>
              <label className="check"><input type="checkbox" name="ci:ingest" defaultChecked /> Ingest</label>
              <button className="btn" type="submit">Create key</button>
            </div>
          </form>
          {token ? (
            <div className="stack-list">
              <Alert tone="info">Copy this key now. It will not be shown again.</Alert>
              <pre className="token">{`{
  "mcpServers": {
    "northline": {
      "url": "https://jira.testcase.pro/mcp",
      "headers": { "Authorization": "Bearer ${token}" }
    }
  }
}`}</pre>
            </div>
          ) : null}
          {initial.length === 0 ? <div className="panel"><Empty title="No API keys" body="Create a key for an MCP client or a CI job. Send it as Authorization: Bearer tcm_…." /></div> : (
            <div className="panel">
              <table className="data-table">
                <thead><tr><th>Name</th><th>Prefix</th><th>Scopes</th><th /></tr></thead>
                <tbody>
                  {initial.map((key) => (
                    <tr key={key.id}>
                      <td>{key.name}</td>
                      <td className="mono">{key.prefix}…</td>
                      <td>{key.scopes.join(", ")}</td>
                      <td><button className="btn-ghost" type="button" onClick={async () => { await api(`/api/v1/api-keys/${key.id}`, { method: "DELETE" }); reload(); }}>Revoke</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SessionsPage({ project, path, initial, reload }: { project: Project; path: string; initial: SessionRow[]; reload: () => void }) {
  const [error, setError] = useState("");
  return (
    <div className="canvas">
      <PageHead title="Configuration" description="Exploratory sessions keep the charter next to the notes." />
      <div className="config">
        <ConfigNav path={path} />
        <div className="stack-list">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <form
            className="panel stack-list"
            style={{ padding: 16 }}
            onSubmit={async (event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              await api(`/api/v1/projects/${project.jira_project_id}/sessions`, {
                method: "POST",
                body: { charter: String(data.get("charter")), mission: String(data.get("mission") || "") },
              });
              event.currentTarget.reset();
              reload();
            }}
          >
            <Field name="charter" label="Charter" required />
            <Field name="mission" label="Mission" />
            <button className="btn" type="submit">Start session</button>
          </form>
          {initial.length === 0 ? <div className="panel"><Empty title="No sessions" body="Write the mission before the session, then keep notes beside it." /></div> : initial.map((session) => (
            <article key={session.id} className="panel" style={{ padding: 16 }}>
              <div className="case-bar">
                <h2 style={{ fontSize: 16 }}>{session.charter}</h2>
                <Lozenge value={session.status} />
              </div>
              <p className="muted">{session.mission}</p>
              <label className="stack" style={{ marginTop: 8 }}>
                Notes
                <textarea
                  className="field"
                  defaultValue={session.notes}
                  aria-label={`Notes for ${session.charter}`}
                  onBlur={async (event) => {
                    await api(`/api/v1/sessions/${session.id}`, { method: "PATCH", body: { notes: event.target.value, status: "OPEN" } });
                  }}
                />
              </label>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

function SharedStepsPage({ project, path, initial, reload }: { project: Project; path: string; initial: SharedRow[]; reload: () => void }) {
  const [title, setTitle] = useState("");
  const [action, setAction] = useState("");
  const [error, setError] = useState("");
  return (
    <div className="canvas">
      <PageHead title="Configuration" description="Updating a shared step increments its version. Cases that call it show the library text." />
      <div className="config">
        <ConfigNav path={path} />
        <div className="stack-list">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <form
            className="toolbar panel"
            onSubmit={async (event) => {
              event.preventDefault();
              await api(`/api/v1/projects/${project.jira_project_id}/shared-steps`, { method: "POST", body: { title, steps: [{ action, expected: "" }] } });
              setTitle("");
              setAction("");
              reload();
            }}
          >
            <input className="field" aria-label="Shared step title" placeholder="Title" value={title} onChange={(event) => setTitle(event.target.value)} required />
            <input className="field" aria-label="Shared step action" placeholder="Action" value={action} onChange={(event) => setAction(event.target.value)} required />
            <button className="btn" type="submit">Add</button>
          </form>
          {initial.length === 0 ? <div className="panel"><Empty title="No shared steps" body="Put a repeated sign-in or setup sequence here, then call it from a case." /></div> : (
            <div className="panel">
              <table className="data-table">
                <thead><tr><th>Title</th><th>Version</th><th>Steps</th></tr></thead>
                <tbody>
                  {initial.map((row) => (
                    <tr key={row.id}>
                      <td>{row.title}</td>
                      <td>v{row.latestVersion}</td>
                      <td>{row.steps.map((step) => step.action).join(" · ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingsPage({
  path,
  project,
  reviewOn,
  reload,
  initial,
}: {
  path: string;
  project: Project;
  reviewOn: boolean;
  reload: () => void;
  initial: FieldRow[];
}) {
  const [error, setError] = useState("");
  return (
    <div className="canvas">
      <PageHead title="Configuration" description="Review mode sends case edits to the approval queue instead of publishing a new version immediately." />
      <div className="config">
        <ConfigNav path={path} />
        <div className="stack-list">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <section className="panel" style={{ padding: 16 }}>
            <h2 style={{ fontSize: 16, marginBottom: 8 }}>Workflow</h2>
            <label className="check">
              <input
                type="checkbox"
                checked={reviewOn}
                onChange={async (event) => {
                  await api("/api/v1/tenant", { method: "PATCH", body: { reviewWorkflowEnabled: event.target.checked } });
                  reload();
                }}
              />
              Require review before changes become the active version
            </label>
          </section>
          <section className="panel">
            <header>Custom fields</header>
            <form
              className="toolbar"
              onSubmit={async (event) => {
                event.preventDefault();
                const data = new FormData(event.currentTarget);
                await api(`/api/v1/projects/${project.jira_project_id}/custom-fields`, {
                  method: "POST",
                  body: { fieldKey: String(data.get("fieldKey")), label: String(data.get("label")), fieldType: String(data.get("fieldType")) },
                });
                event.currentTarget.reset();
                reload();
              }}
            >
              <input className="field" name="fieldKey" placeholder="Field key" aria-label="Field key" required />
              <input className="field" name="label" placeholder="Label" aria-label="Field label" required />
              <select name="fieldType" className="field" aria-label="Field type">
                <option value="text">Text</option>
                <option value="select">Select</option>
                <option value="number">Number</option>
              </select>
              <button className="btn" type="submit">Add field</button>
            </form>
            {initial.length === 0 ? <Empty title="No custom fields" body="Add a field when the library needs data Jira does not already store." /> : (
              <table className="data-table">
                <thead><tr><th>Label</th><th>Key</th><th>Type</th></tr></thead>
                <tbody>
                  {initial.map((field) => (
                    <tr key={field.field_key}>
                      <td>{field.label}</td>
                      <td className="mono">{field.field_key}</td>
                      <td>{labelize(field.field_type)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function IssuePage({
  issueKey,
  project,
  navigate,
  initial,
}: {
  issueKey: string;
  project: Project;
  navigate: (to: string) => void;
  initial: CoveragePayload;
}) {
  return (
    <div className="canvas">
      <PageHead title={issueKey} description="Linked tests and the latest execution. Coverage is written to the tcmCoverage issue property for JQL." />
      <CoverageView
        issueKey={issueKey}
        projectId={project.jira_project_id}
        projectKey={project.jira_project_key}
        initial={initial}
        onOpenCase={(testKey) => navigate(`/cases/${testKey}`)}
      />
    </div>
  );
}

function CoverageView({
  issueKey,
  projectId,
  projectKey,
  onOpenCase,
  initial,
}: {
  issueKey: string;
  projectId: string;
  projectKey: string;
  onOpenCase?: (testKey: string) => void;
  initial?: CoveragePayload | null;
}) {
  const [coverage, setCoverage] = useState<CoveragePayload | null>(initial ?? null);
  const [error, setError] = useState("");
  const [testKey, setTestKey] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setCoverage(await api(`/api/v1/issues/${encodeURIComponent(issueKey)}/coverage`));
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No coverage for this issue.");
    }
  }, [issueKey]);
  useEffect(() => {
    if (initial) {
      setCoverage(initial);
      return;
    }
    void load();
  }, [initial, load]);
  const cases = useMemo(() => [...(coverage?.cases ?? [])].sort((a, b) => compareTestKeys(a.testKey, b.testKey)), [coverage]);
  return (
    <div className="stack-list">
      {!coverage && !error ? <Loading label="Loading coverage…" /> : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {coverage ? (
        <>
          <div className="case-bar">
            <Lozenge value={coverage.status} />
            <Lozenge value="PASS">{coverage.passedCount} passed</Lozenge>
            <Lozenge value={coverage.failedCount > 0 ? "FAIL" : "neutral"}>{coverage.failedCount} failed</Lozenge>
            <span className="muted">{coverage.totalLinkedTests} linked{coverage.hasFlakyTests === "true" ? " · flaky" : ""}</span>
          </div>
          <div className="panel">
            {cases.length === 0 ? <Empty title="No linked tests" body="This issue is uncovered. Link a test key to record coverage." /> : cases.map((testCase) => (
              <div key={testCase.testKey} className="link-row">
                {onOpenCase ? (
                  <button className="btn-subtle" type="button" onClick={() => onOpenCase(testCase.testKey)}>{testCase.testKey}</button>
                ) : (
                  <span className="mono">{testCase.testKey}</span>
                )}
                <span className="grow clip">{testCase.title}</span>
                <Lozenge value={testCase.status} />
                <button
                  className="btn-ghost"
                  type="button"
                  disabled={busy}
                  aria-label={`Unlink ${testCase.testKey}`}
                  onClick={async () => {
                    setBusy(true);
                    setError("");
                    try {
                      await api(`/api/v1/issues/links?issueKey=${encodeURIComponent(issueKey)}&testKey=${encodeURIComponent(testCase.testKey)}`, { method: "DELETE" });
                      await load();
                    } catch (err) {
                      setError(err instanceof Error ? err.message : "Could not unlink that test.");
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Unlink
                </button>
              </div>
            ))}
          </div>
          <form
            className="toolbar panel"
            onSubmit={async (event) => {
              event.preventDefault();
              if (!projectId || !projectKey) {
                setError("This panel has no Jira project context, so a test cannot be linked from here.");
                return;
              }
              setBusy(true);
              setError("");
              try {
                await api("/api/v1/issues/links", { method: "POST", body: { projectId, projectKey, issueKey, testKey: testKey.trim() } });
                setTestKey("");
                await load();
              } catch (err) {
                setError(err instanceof Error ? err.message : "Could not link that test.");
              } finally {
                setBusy(false);
              }
            }}
          >
            <label className="stack">
              Link test key
              <input className="field" value={testKey} onChange={(event) => setTestKey(event.target.value)} placeholder="FIN-T1" required />
            </label>
            <button className="btn" type="submit" disabled={busy || !projectId}>Link</button>
          </form>
        </>
      ) : null}
    </div>
  );
}

function SharedStepBody({ step, index }: { step: Step; index: number }) {
  const shared = step.shared;
  return (
    <div>
      <p className="muted">Shared step{shared ? ` · ${shared.title} · v${shared.version}` : ""} · step {index + 1}</p>
      <p className="muted">Edit the source under Configuration → Shared steps. Saving this case keeps the reference.</p>
      {shared && shared.steps.length > 0 ? (
        <ol style={{ margin: "8px 0 0", paddingLeft: 18 }}>
          {shared.steps.map((inner) => (
            <li key={inner.order}>
              <strong>{inner.action}</strong>
              <div className="muted">Data: {inner.data || "None"} · Expected: {inner.expected || "None"}</div>
            </li>
          ))}
        </ol>
      ) : (
        <p>{step.action || "This shared step has no actions yet."}</p>
      )}
    </div>
  );
}

function SharePage({ token, theme, setTheme }: { token: string; theme: Theme; setTheme: (theme: Theme) => void }) {
  const [report, setReport] = useState<null | { label: string | null; projectKey: string | null; projectName: string | null; cycle: { cycleKey: string; name: string; rollup: { pass: number; fail: number; total: number } } | null }>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api<NonNullable<typeof report>>(`/api/v1/public/reports/${token}`)
      .then(setReport)
      .catch((err: Error) => setError(err.message));
  }, [token]);
  return (
    <article className="canvas" style={{ maxWidth: 720, margin: "0 auto" }}>
      <div className="case-bar">
        <strong>Northline report</strong>
        <span className="spacer" />
        <ThemeSwitch theme={theme} setTheme={setTheme} />
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {report ? (
        <>
          <h1 style={{ fontSize: 24, fontWeight: 650 }}>{report.label ?? "Test report"}</h1>
          <p className="muted" style={{ marginTop: 8 }}>{report.projectName} · {report.projectKey}</p>
          {report.cycle ? (
            <p style={{ marginTop: 16 }}>
              {report.cycle.cycleKey} {report.cycle.name}: {report.cycle.rollup.pass} passed, {report.cycle.rollup.fail} failed, {report.cycle.rollup.total} total.
            </p>
          ) : (
            <p style={{ marginTop: 16 }}>This link has no run attached.</p>
          )}
        </>
      ) : null}
    </article>
  );
}

function useHash() {
  const read = () => {
    const raw = window.location.hash.replace(/^#/, "") || "/";
    const path = raw.startsWith("/") ? raw : `/${raw}`;
    return path.split("?")[0] || "/";
  };
  const [path, setPath] = useState(read);
  useEffect(() => {
    const onChange = () => setPath(read());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  const navigate = useCallback((to: string) => {
    const next = to.startsWith("/") ? to : `/${to}`;
    window.location.hash = next;
    setPath(next.split("?")[0] || "/");
  }, []);
  return { path, navigate };
}
